import { useEffect, useState } from "react";
import AppLayout from '@/components/feature/AppLayout';
import { useAuth } from '@/context/AuthContext';
import LocationPicker, {
  type SelectedLocation,
} from "@/components/feature/LocationPicker";

export default function VReportPage() {
  const [step, setStep] = useState(1);
  const [incident, setIncident] = useState("");
  const [urgency, setUrgency] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [childFirstName, setChildFirstName] = useState("");
  const [childMiddleName, setChildMiddleName] = useState("");
  const [childLastName, setChildLastName] = useState("");
  const [childNickname, setChildNickname] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("");
  const [nationality, setNationality] = useState("Filipino");
  const [religion, setReligion] = useState("");
  const [placeOfBirth, setPlaceOfBirth] = useState("");
  const [address, setAddress] = useState("");
  const [barangay, setBarangay] = useState("");
  const [municipality, setMunicipality] = useState("");
  const [province, setProvince] = useState("");
  const [incidentLatitude, setIncidentLatitude] =
  useState<number | null>(null);
  const [incidentLongitude, setIncidentLongitude] =
  useState<number | null>(null);
  const [schoolName, setSchoolName] = useState("");
  const [gradeLevel, setGradeLevel] = useState("");
  const [sectionName, setSectionName] = useState("");
  const [fatherName, setFatherName] = useState("");
  const [motherName, setMotherName] = useState("");
  const [guardianName, setGuardianName] = useState("");
  const [guardianRelationship, setGuardianRelationship] = useState("");
  const [guardianContact, setGuardianContact] = useState("");
  const [guardianAddress, setGuardianAddress] = useState("");
  const [medicalConditions, setMedicalConditions] = useState("");
  const [medications, setMedications] = useState("");
  const [withDisability, setWithDisability] = useState("No");
  const [childInfo, setChildInfo] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [reporter, setReporter] = useState("");
  const [contact, setContact] = useState("");
  const [reporterRelation, setReporterRelation] = useState("");
  const [reportSource, setReportSource] = useState("Victim Mobile Application");
  const [reporterNotes, setReporterNotes] = useState("");
  const [photos, setPhotos] = useState<string[]>([]);
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [videoPreview, setVideoPreview] = useState<string>("");
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const [shouldCallAfterSubmit, setShouldCallAfterSubmit] = useState(false);

  const [assignedOffice, setAssignedOffice] = useState<{
    name: string;
    location: string;
    reportUid: string;
  } | null>(null);

  // Admin-configured case categories
  const [incidentTypes, setIncidentTypes] = useState<any[]>([]);
  const [loadingIncidentTypes, setLoadingIncidentTypes] = useState(true);
  const [aiTesting, setAiTesting] = useState(false);
  const [aiResult, setAiResult] = useState<{
    suggested_category?: string;
    category_confidence?: number;
    suggested_urgency?: string;
    urgency_confidence?: number;
    reason?: string;
    requires_social_worker_validation?: boolean;
    ai_provider?: string;
    ai_model?: string;
  } | null>(null);

  const { user } = useAuth();
  

  const next = () => setStep((prev) => Math.min(prev + 1, 4));
  const back = () => setStep((prev) => Math.max(prev - 1, 1));

  // Load active Case Categories from Admin > System Configuration.
  // The Victim Report must always use the categories configured by Admin.
  useEffect(() => {
    const loadIncidentTypes = async () => {
      try {
        setLoadingIncidentTypes(true);

        const response = await fetch(
          "https://guardiancloudcare.com/backend/api/get_system_config.php"
        );

        if (!response.ok) {
          throw new Error(`HTTP error ${response.status}`);
        }

        const result = await response.json();

        if (!result.success || !Array.isArray(result.data)) {
          throw new Error(
            result.error || "Invalid system configuration response"
          );
        }

        const categoryConfig = result.data.find(
          (config: any) => config.config_key === "case_categories"
        );

        if (!categoryConfig || !Array.isArray(categoryConfig.config_value)) {
          setIncidentTypes([]);
          return;
        }

        const activeCategories = categoryConfig.config_value
          .filter((item: any) => {
            // Supports old database format: "Physical Abuse"
            if (typeof item === "string") return true;

            // Supports current Admin format with active flag
            return item && item.active !== false;
          })
          .map((item: any, index: number) => {
            if (typeof item === "string") {
              return {
                id: `category-${index}`,
                name: item,
                code: item
                  .toUpperCase()
                  .replace(/[^A-Z0-9]+/g, "_")
                  .replace(/^_+|_+$/g, ""),
                icon: "ri-question-line",
              };
            }

            return {
              ...item,
              id: item.id || `category-${index}`,
              name: item.name || "",
              code:
                item.code ||
                String(item.name || "")
                  .toUpperCase()
                  .replace(/[^A-Z0-9]+/g, "_")
                  .replace(/^_+|_+$/g, ""),
              icon: item.icon || "ri-question-line",
            };
          })
          .filter((item: any) => item.name);

        setIncidentTypes(activeCategories);

      } catch (error) {
        console.error("Failed to load incident categories:", error);
        setIncidentTypes([]);
      } finally {
        setLoadingIncidentTypes(false);
      }
    };

    loadIncidentTypes();
  }, []);

  const testAIClassification = async () => {
    if (!description.trim()) {
      alert("Please describe what happened first.");
      return;
    }

    try {
      setAiTesting(true);
      setAiResult(null);

      const aiRequest = fetch(
        "https://guardiancloudcare.com/backend/api/ai_classify_report.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            description: description.trim(),
          }),
        }
      );

      // Keep the AI loading animation visible for at least 5 seconds.
      const minimumLoadingTime = new Promise((resolve) => {
        setTimeout(resolve, 5000);
      });

      const [response] = await Promise.all([
        aiRequest,
        minimumLoadingTime,
      ]);

      const text = await response.text();

      let result: any;

      try {
        result = JSON.parse(text);
      } catch {
        console.error("AI raw response:", text);
        throw new Error("AI server returned an invalid response.");
      }

      if (!response.ok || !result.success) {
        throw new Error(
          result.error || "AI classification request failed."
        );
      }

      setAiResult({
        suggested_category: result.suggested_category || "",
        category_confidence: Number(
          result.category_confidence ?? 0
        ),
        suggested_urgency: result.suggested_urgency || "",
        urgency_confidence: Number(
          result.urgency_confidence ?? 0
        ),
        reason: result.reason || "",
        requires_social_worker_validation:
          Boolean(result.requires_social_worker_validation),
        ai_provider: result.ai_provider || "Groq",
        ai_model:
          result.ai_model || "openai/gpt-oss-20b",
      });
    } catch (error: any) {
      console.error("AI Classification Error:", error);

      alert(
        error?.message ||
          "Unable to connect to the AI classification service."
      );
    } finally {
      setAiTesting(false);
    }
  };

  useEffect(() => {
      if (!birthDate) {
          setAge("");
          return;
      }

      const birth = new Date(birthDate);
      const today = new Date();
      let years = today.getFullYear() - birth.getFullYear();
      const month = today.getMonth() - birth.getMonth();

      if (
          month < 0 ||
          (month === 0 && today.getDate() < birth.getDate())
      ) {
          years--;
      }

      setAge(String(years));
  }, [birthDate]);

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) {
      return `${bytes} B`;
    }

    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }

    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const handlePhotos = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    if (files.length === 0) return;

    const remainingSlots = 5 - photoFiles.length;

    if (remainingSlots <= 0) return;

    const limitedFiles = files.slice(0, remainingSlots);

    setPhotoFiles((prev) => [...prev, ...limitedFiles]);

    const previews = limitedFiles.map((file) => URL.createObjectURL(file));

    setPhotos((prev) => [...prev, ...previews]);

    e.target.value = '';
  };

  const handleVideo = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) return;

    // Maximum video size: 50 MB
    const maxSize = 50 * 1024 * 1024;

    if (file.size > maxSize) {
      alert("Video must not exceed 50 MB.");
      e.target.value = "";
      return;
    }

    // Allowed video types
    const allowedTypes = [
      "video/mp4",
      "video/webm",
      "video/quicktime",
      "video/x-msvideo",
    ];

    if (!allowedTypes.includes(file.type)) {
      alert("Please upload an MP4, WEBM, MOV, or AVI video.");
      e.target.value = "";
      return;
    }

    // Remove previous video preview
    if (videoPreview) {
      URL.revokeObjectURL(videoPreview);
    }

    const preview = URL.createObjectURL(file);

    setVideoFile(file);
    setVideoPreview(preview);

    e.target.value = "";
  };

  const submitReport = async () => {
    setSubmitMessage('');

    setSubmitting(true);

    const formData = new FormData();

    formData.append('victim_uid', user?.uid || '');
    formData.append('incident_type', incident);
    formData.append('urgency', urgency);
    formData.append('description', description);
    formData.append('location', location);
    formData.append("child_first_name", childFirstName);
    formData.append("child_middle_name", childMiddleName);
    formData.append("child_last_name", childLastName);
    formData.append("child_nickname", childNickname);
    formData.append("birth_date", birthDate);
    formData.append("age", age);
    formData.append("gender", gender);
    formData.append("nationality", nationality);
    formData.append("religion", religion);
    formData.append("place_of_birth", placeOfBirth);
    formData.append("address", address);
    formData.append("barangay", barangay);
    formData.append("municipality", municipality);
    formData.append("province", province);
    formData.append(
      "incident_latitude",
      incidentLatitude !== null ? String(incidentLatitude) : ""
    );

    formData.append(
      "incident_longitude",
      incidentLongitude !== null ? String(incidentLongitude) : ""
    );
    formData.append("school_name", schoolName);
    formData.append("grade_level", gradeLevel);
    formData.append("section_name", sectionName);
    formData.append("father_name", fatherName);
    formData.append("mother_name", motherName);
    formData.append("guardian_name", guardianName);
    formData.append("guardian_relationship", guardianRelationship);
    formData.append("guardian_contact", guardianContact);
    formData.append("guardian_address", guardianAddress);
    formData.append("medical_conditions", medicalConditions);
    formData.append("medications", medications);
    formData.append("with_disability", withDisability);
    formData.append("child_info", childInfo);
    formData.append("is_anonymous", anonymous ? "1" : "0");
    formData.append("reporter_name", anonymous ? "" : reporter);
    formData.append("reporter_contact", anonymous ? "" : contact);

    formData.append("reporter_relation", reporterRelation);
    formData.append("report_source", reportSource);
    formData.append("reporter_notes", reporterNotes);

    photoFiles.forEach((file) => {
      formData.append("photos[]", file);
    });

    if (videoFile) {
      formData.append("video", videoFile);
    }

    try {
      const res = await fetch('https://guardiancloudcare.com/backend/api/submit_victim_report.php', {
        method: 'POST',
        body: formData,
      });

      const text = await res.text();

      let result: any;

      try {
        result = JSON.parse(text);
      } catch {
        console.error("Invalid PHP response:", text);
        setSubmitMessage("The server returned an invalid response.");
        return;
      }

      if (!res.ok || !result.success) {
        setSubmitMessage(
          result.message || result.error || "Failed to submit the report."
        );
        return;
      }

      setSubmitMessage("");

      setAssignedOffice({
        name: result.assigned_office_name || "DSWD office not assigned yet",
        location: result.assigned_office_location || "",
        reportUid: result.report_uid || "",
      });

    } catch (err) {
      console.error(err);
      setSubmitMessage("Server error.");
    } finally {
      setSubmitting(false);
    }
  };

  const emergencyContacts = [
    { name: 'Siniloan Emergency', number: '09194173698', lat: 14.4217, lng: 121.4469 },
    { name: 'Pangil Emergency', number: '09194173698', lat: 14.4033, lng: 121.4656 },
    { name: 'Pakil Emergency', number: '09194173698', lat: 14.3823, lng: 121.4786 },
    { name: 'Paete Emergency', number: '09194173698', lat: 14.3647, lng: 121.4826 },
    { name: 'Kalayaan MDRRMO', number: '09169049459', lat: 14.3500, lng: 121.5667 },
    { name: 'Lumban Emergency', number: '09194173698', lat: 14.2973, lng: 121.4591 },
    { name: 'Pagsanjan Emergency', number: '09194173698', lat: 14.2732, lng: 121.4553 },
    { name: 'Sta. Cruz Emergency', number: '09194173698', lat: 14.2814, lng: 121.4161 },
  ];

  const getDistance = (lat1: number, lng1: number, lat2: number, lng2: number) => {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);

    return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
  };

  const callNearestEmergency = () => {
    if (!navigator.geolocation) {
      window.location.href = 'tel:09194173698';
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const userLat = position.coords.latitude;
        const userLng = position.coords.longitude;

        const nearest = emergencyContacts.reduce((prev, current) => {
          const prevDistance = getDistance(userLat, userLng, prev.lat, prev.lng);
          const currentDistance = getDistance(userLat, userLng, current.lat, current.lng);

          return currentDistance < prevDistance ? current : prev;
        });

        window.location.href = `tel:${nearest.number}`;
      },
      () => {
        window.location.href = 'tel:09194173698';
      }
    );
  };

  return (
    <AppLayout title="Report Incident" breadcrumb={['Victim', 'Report Incident']}>
      <div className="max-w-3xl mx-auto bg-white rounded-3xl border border-gray-100 shadow-sm p-4 md:p-6 space-y-5">

        <div className="flex items-center gap-3">
          {step > 1 && (
            <button onClick={back} className="w-9 h-9 rounded-full hover:bg-gray-100">
              <i className="ri-arrow-left-line text-lg" />
            </button>
          )}
          <h1 className="text-lg font-bold text-gray-800">Report Incident</h1>
        </div>

        <StepIndicator step={step} />

        <EmergencyBox onCall={callNearestEmergency} />

        {step === 1 && (
          <>
            <h2 className="font-semibold text-gray-800">What type of incident are you reporting?</h2>

            {loadingIncidentTypes ? (
              <div className="rounded-2xl border border-gray-100 bg-gray-50 p-5 text-center text-sm text-gray-500">
                Loading incident categories...
              </div>
            ) : incidentTypes.length === 0 ? (
              <div className="rounded-2xl border border-red-100 bg-red-50 p-5 text-center">
                <p className="font-semibold text-red-700">
                  No incident categories are currently available.
                </p>
                <p className="text-xs text-red-500 mt-1">
                  Please ask the Administrator to enable a Case Category.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {incidentTypes.map((item: any, index: number) => {
                  const title = item.name || "Other Concern";
                  const icon = item.icon || "ri-question-line";

                  return (
                    <button
                      key={item.id || item.code || `${title}-${index}`}
                      type="button"
                      onClick={() => setIncident(title)}
                      className={`p-4 rounded-2xl border text-left flex items-center gap-3 transition ${
                        incident === title
                          ? "border-orange-400 bg-orange-50"
                          : "border-gray-100 hover:border-orange-200"
                      }`}
                    >
                      <div className="w-9 h-9 rounded-xl bg-gray-50 flex items-center justify-center">
                        <i className={`${icon} text-gray-500`} />
                      </div>
                      <span className="font-medium text-sm text-gray-700">
                        {title}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            <h2 className="font-semibold text-gray-800">How urgent is this?</h2>

            <UrgencyCard
              active={urgency === 'Immediate Danger'}
              color="red"
              title="Immediate Danger"
              desc="The child is in immediate risk of harm — emergency call will happen after report submission"
              onClick={() => {
                setUrgency('Immediate Danger');
                setShouldCallAfterSubmit(true);
              }}
            />

            <UrgencyCard
              active={urgency === 'Urgent'}
              color="yellow"
              title="Urgent"
              desc="Needs attention within 24 hours"
              onClick={() => {
                setUrgency('Urgent');
                setShouldCallAfterSubmit(false);
              }}
            />

            <UrgencyCard
              active={urgency === 'Non-Urgent'}
              color="green"
              title="Non-Urgent"
              desc="Concern that should be addressed but not time-critical"
              onClick={() => {
                setUrgency('Non-Urgent');
                setShouldCallAfterSubmit(false);
              }}
            />

            <button
              onClick={next}
              disabled={!incident || !urgency}
              className="w-full bg-orange-300 disabled:bg-orange-200 text-white font-bold py-4 rounded-2xl"
            >
              Next
            </button>
          </>
        )}

        {step === 2 && (
          <>
            {/* INCIDENT DETAILS */}
            <div className="bg-white border border-gray-100 rounded-2xl p-5">
              <h2 className="text-lg font-bold text-gray-800 mb-4">
                Incident Details
              </h2>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Describe what happened *
                </label>
                <textarea
                  value={description}
                  maxLength={500}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Explain the incident in detail..."
                  className="w-full h-36 rounded-2xl border border-gray-200 p-4 resize-none outline-none focus:ring-2 focus:ring-orange-200"
                />
                <p className="text-right text-xs text-gray-400 mt-1">
                  {description.length}/500
                </p>

                <button
                  type="button"
                  onClick={testAIClassification}
                  disabled={aiTesting}
                  className={`mt-4 w-full py-4 rounded-xl font-semibold transition flex items-center justify-center gap-3 ${
                    aiTesting
                      ? "bg-orange-100 text-orange-600 cursor-wait"
                      : "border border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-100"
                  }`}
                >
                  {aiTesting ? (
                    <>
                      <span className="relative flex items-center justify-center">
                        <span className="absolute w-7 h-7 rounded-full border-2 border-orange-200 border-t-orange-600 animate-spin" />
                        <i className="ri-sparkling-2-line text-orange-500 text-sm" />
                      </span>

                      <span>AI is analyzing the report...</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-sparkling-2-line text-lg" />
                      <span>AI Classification</span>
                    </>
                  )}
                </button>

                {aiResult && (
                <div className="mt-4 rounded-2xl border border-orange-200 bg-orange-50 p-5">

                  {/* AI HEADER */}
                  <div className="flex items-start justify-between gap-4 mb-5">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center">
                          <i className="ri-sparkling-2-line text-orange-600" />
                        </span>

                        <div>
                          <p className="text-xs uppercase tracking-wide text-orange-600 font-bold">
                            AI-Assisted Classification
                          </p>

                          <p className="text-xs text-gray-500 mt-0.5">
                            Powered by {aiResult.ai_provider || "Groq"} •{" "}
                            {aiResult.ai_model || "openai/gpt-oss-20b"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* CATEGORY */}
                  <div className="bg-white rounded-xl p-4 mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase">
                          Suggested Category
                        </p>

                        <p className="text-base font-bold text-gray-800 mt-1">
                          {aiResult.suggested_category || "Not available"}
                        </p>
                      </div>

                      <span className="text-sm font-bold text-orange-600">
                        {aiResult.category_confidence ?? 0}%
                      </span>
                    </div>

                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-orange-500 rounded-full transition-all"
                        style={{
                          width: `${Math.min(
                            100,
                            Math.max(0, aiResult.category_confidence ?? 0)
                          )}%`,
                        }}
                      />
                    </div>

                    <p className="text-xs text-gray-400 mt-2">
                      AI confidence
                    </p>
                  </div>

                  {/* URGENCY */}
                  <div className="bg-white rounded-xl p-4 mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase">
                          Suggested Urgency
                        </p>

                        <p className="text-base font-bold text-gray-800 mt-1">
                          {aiResult.suggested_urgency || "Not available"}
                        </p>
                      </div>

                      <span className="text-sm font-bold text-orange-600">
                        {aiResult.urgency_confidence ?? 0}%
                      </span>
                    </div>

                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-orange-500 rounded-full transition-all"
                        style={{
                          width: `${Math.min(
                            100,
                            Math.max(0, aiResult.urgency_confidence ?? 0)
                          )}%`,
                        }}
                      />
                    </div>

                    <p className="text-xs text-gray-400 mt-2">
                      AI confidence
                    </p>
                  </div>

                  {/* AI EXPLANATION */}
                  <div className="bg-white rounded-xl p-4 mb-3">
                    <div className="flex items-center gap-2 mb-2">
                      <i className="ri-information-line text-orange-500" />

                      <p className="text-xs font-semibold text-gray-500 uppercase">
                        AI Explanation
                      </p>
                    </div>

                    <p className="text-sm text-gray-700 leading-relaxed">
                      {aiResult.reason || "No explanation was provided by the AI."}
                    </p>
                  </div>

                  {/* VALIDATION WARNING */}
                  {aiResult.requires_social_worker_validation && (
                    <div className="flex gap-3 bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                      <i className="ri-shield-check-line text-yellow-600 text-lg mt-0.5" />

                      <div>
                        <p className="text-sm font-bold text-yellow-800">
                          Social Worker Validation Required
                        </p>

                        <p className="text-xs text-yellow-700 mt-1 leading-relaxed">
                          The AI result is provided only as decision support.
                          The Social Worker must review and validate the
                          suggested classification and urgency before proceeding.
                        </p>
                      </div>
                    </div>
                  )}

                </div>
              )}
              </div>
              <div className="mt-5">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Incident Location *
                </label>
                <input
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Complete location..."
                  className="w-full border border-gray-200 rounded-2xl p-4 outline-none focus:ring-2 focus:ring-orange-200"
                />
              </div>
            </div>
            {/* CHILD INFORMATION */}
            <div className="bg-white border border-gray-100 rounded-2xl p-5">
              <h2 className="text-lg font-bold text-gray-800 mb-5">
                Child Information
              </h2>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-semibold">
                    First Name
                  </label>
                  <input
                    value={childFirstName}
                    onChange={(e)=>setChildFirstName(e.target.value)}
                    className="mt-2 w-full border rounded-xl p-3"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold">
                    Middle Name
                  </label>
                  <input
                    value={childMiddleName}
                    onChange={(e)=>setChildMiddleName(e.target.value)}
                    className="mt-2 w-full border rounded-xl p-3"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold">
                    Last Name
                  </label>
                  <input
                    value={childLastName}
                    onChange={(e)=>setChildLastName(e.target.value)}
                    className="mt-2 w-full border rounded-xl p-3"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4 mt-5">
                <div>
                  <label className="text-sm font-semibold">
                    Nickname
                  </label>
                  <input
                    value={childNickname}
                    onChange={(e)=>setChildNickname(e.target.value)}
                    className="mt-2 w-full border rounded-xl p-3"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold">
                    Birth Date
                  </label>
                  <input
                    type="date"
                    value={birthDate}
                    onChange={(e)=>setBirthDate(e.target.value)}
                    className="mt-2 w-full border rounded-xl p-3"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-4 mt-5">
                <div>
                  <label className="text-sm font-semibold">
                    Age
                  </label>
                  <input
                    value={age}
                    readOnly
                    className="mt-2 w-full border rounded-xl p-3 bg-gray-100"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold">
                    Gender
                  </label>
                  <select
                    value={gender}
                    onChange={(e)=>setGender(e.target.value)}
                    className="mt-2 w-full border rounded-xl p-3"
                  >
                    <option value="">Select</option>
                    <option>Male</option>
                    <option>Female</option>
                  </select>

                </div>
                <div>
                  <label className="text-sm font-semibold">
                    Nationality
                  </label>
                  <input
                    value={nationality}
                    onChange={(e)=>setNationality(e.target.value)}
                    className="mt-2 w-full border rounded-xl p-3"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4 mt-5">
                <div>
                  <label className="text-sm font-semibold">
                    Religion
                  </label>
                  <input
                    value={religion}
                    onChange={(e)=>setReligion(e.target.value)}
                    className="mt-2 w-full border rounded-xl p-3"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold">
                    Place of Birth
                  </label>
                  <input
                    value={placeOfBirth}
                    onChange={(e)=>setPlaceOfBirth(e.target.value)}
                    className="mt-2 w-full border rounded-xl p-3"
                  />
                </div>
              </div>
              <div className="mt-5">
                <label className="text-sm font-semibold">
                  Additional Child Information
                </label>
                <textarea
                  value={childInfo}
                  onChange={(e)=>setChildInfo(e.target.value)}
                  placeholder="Special marks, physical appearance, additional information..."
                  className="mt-2 w-full h-28 border rounded-xl p-3 resize-none"
                />
              </div>
              {/* ADDRESS INFORMATION */}
              <div className="bg-white border border-gray-100 rounded-2xl p-5 mt-5">
                <h2 className="text-lg font-bold text-gray-800 mb-5">
                  Address Information
                </h2>
                <div>
                  <label className="text-sm font-semibold">
                    Complete Address
                  </label>
                  <textarea
                    value={address}
                    onChange={(e)=>setAddress(e.target.value)}
                    rows={3}
                    className="mt-2 w-full border rounded-xl p-3 resize-none"
                  />
                </div>
                <div className="mb-5">
                  <LocationPicker
                    onLocationSelect={(selected) => {
                      setIncidentLatitude(selected.latitude);
                      setIncidentLongitude(selected.longitude);

                      // Update only the Complete Address field.
                      setAddress(selected.address);
                      setLocation(selected.address);
                    }}
                  />
                </div>
                <div className="grid md:grid-cols-3 gap-4 mt-5">
                  <div>
                    <label className="text-sm font-semibold">
                      Barangay
                    </label>
                    <input
                      value={barangay}
                      onChange={(e)=>setBarangay(e.target.value)}
                      className="mt-2 w-full border rounded-xl p-3"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold">
                      Municipality
                    </label>
                    <input
                      value={municipality}
                      onChange={(e)=>setMunicipality(e.target.value)}
                      className="mt-2 w-full border rounded-xl p-3"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold">
                      Province
                    </label>
                    <input
                      value={province}
                      onChange={(e)=>setProvince(e.target.value)}
                      className="mt-2 w-full border rounded-xl p-3"
                    />
                  </div>
                </div>
              </div>
              {/* SCHOOL INFORMATION */}
              <div className="bg-white border border-gray-100 rounded-2xl p-5 mt-5">
                <h2 className="text-lg font-bold text-gray-800 mb-5">
                  School Information
                </h2>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-semibold">
                      School Name
                    </label>
                    <input
                      value={schoolName}
                      onChange={(e)=>setSchoolName(e.target.value)}
                      className="mt-2 w-full border rounded-xl p-3"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold">
                      Grade Level
                    </label>
                    <input
                      value={gradeLevel}
                      onChange={(e)=>setGradeLevel(e.target.value)}
                      className="mt-2 w-full border rounded-xl p-3"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold">
                      Section
                    </label>
                    <input
                      value={sectionName}
                      onChange={(e)=>setSectionName(e.target.value)}
                      className="mt-2 w-full border rounded-xl p-3"
                    />
                  </div>
                </div>
              </div>
              {/* PARENTS */}
              <div className="bg-white border border-gray-100 rounded-2xl p-5 mt-5">
                <h2 className="text-lg font-bold text-gray-800 mb-5">
                  Parent Information
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-semibold">
                      Father's Name
                    </label>
                    <input
                      value={fatherName}
                      onChange={(e)=>setFatherName(e.target.value)}
                      className="mt-2 w-full border rounded-xl p-3"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold">
                      Mother's Name
                    </label>
                    <input
                      value={motherName}
                      onChange={(e)=>setMotherName(e.target.value)}
                      className="mt-2 w-full border rounded-xl p-3"
                    />
                  </div>
                </div>
              </div>
              {/* GUARDIAN */}
              <div className="bg-white border border-gray-100 rounded-2xl p-5 mt-5">
                <h2 className="text-lg font-bold text-gray-800 mb-5">
                  Guardian Information
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-semibold">
                      Guardian Name
                    </label>
                    <input
                      value={guardianName}
                      onChange={(e)=>setGuardianName(e.target.value)}
                      className="mt-2 w-full border rounded-xl p-3"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold">
                      Relationship
                    </label>
                    <input
                      value={guardianRelationship}
                      onChange={(e)=>setGuardianRelationship(e.target.value)}
                      className="mt-2 w-full border rounded-xl p-3"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4 mt-5">
                  <div>
                    <label className="text-sm font-semibold">
                      Guardian Contact
                    </label>
                    <input
                      value={guardianContact}
                      onChange={(e)=>setGuardianContact(e.target.value)}
                      className="mt-2 w-full border rounded-xl p-3"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold">
                      Guardian Address
                    </label>
                    <input
                      value={guardianAddress}
                      onChange={(e)=>setGuardianAddress(e.target.value)}
                      className="mt-2 w-full border rounded-xl p-3"
                    />
                  </div>
                </div>
              </div>
            </div>
            {/* MEDICAL INFORMATION */}
            <div className="bg-white border border-gray-100 rounded-2xl p-5 mt-5">
              <h2 className="text-lg font-bold text-gray-800 mb-5">
                Medical Information
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold">
                    Medical Conditions
                  </label>
                  <textarea
                    rows={3}
                    value={medicalConditions}
                    onChange={(e) => setMedicalConditions(e.target.value)}
                    placeholder="Asthma, Heart Disease, Allergies..."
                    className="mt-2 w-full border rounded-xl p-3 resize-none"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold">
                    Current Medications
                  </label>
                  <textarea
                    rows={3}
                    value={medications}
                    onChange={(e) => setMedications(e.target.value)}
                    placeholder="Medicines currently being taken..."
                    className="mt-2 w-full border rounded-xl p-3 resize-none"
                  />
                </div>
              </div>
              <div className="mt-5">
                <label className="text-sm font-semibold">
                  Child has Disability?
                </label>
                <select
                  value={withDisability}
                  onChange={(e) => setWithDisability(e.target.value)}
                  className="mt-2 w-full border rounded-xl p-3"
                >
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </select>
              </div>
            </div>
            {/* PHOTO EVIDENCE */}
            <div className="bg-white border border-gray-100 rounded-2xl p-5 mt-5">
              <h2 className="text-lg font-bold text-gray-800">
                Photo Evidence
              </h2>
              <p className="text-sm text-gray-500 mt-2">
                Upload up to five (5) images that may help support the report.
              </p>
              {photos.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-5">
                  {photos.map((photo, index) => (
                    <div
                      key={index}
                      className="relative"
                    >
                      <img
                        src={photo}
                        className="w-full h-24 rounded-xl object-cover border"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setPhotos((prev) =>
                            prev.filter((_, i) => i !== index)
                          );
                          setPhotoFiles((prev) =>
                            prev.filter((_, i) => i !== index)
                          );
                        }}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center"
                      >
                        <i className="ri-close-line" />
                      </button>

                    </div>
                  ))}
                </div>
              )}
              {photoFiles.length < 5 && (
                <label className="mt-5 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-2xl p-8 cursor-pointer hover:border-orange-400 transition">
                  <i className="ri-image-add-line text-4xl text-orange-500 mb-2" />
                  <span className="font-medium text-gray-600">
                    Click to Upload Photos
                  </span>
                  <span className="text-xs text-gray-400 mt-1">
                    JPG • PNG • WEBP
                  </span>
                  <span className="text-xs text-orange-500 mt-2">
                    {photoFiles.length}/5 uploaded
                  </span>
                  <input
                    type="file"
                    multiple
                    accept="image/png,image/jpeg,image/webp"
                    onChange={handlePhotos}
                    className="hidden"
                  />
                </label>
              )}
            </div>
            {/* VIDEO EVIDENCE */}
            <div className="bg-white border border-gray-100 rounded-2xl p-5 mt-5">
              <h2 className="text-lg font-bold text-gray-800">
                Video Evidence
              </h2>
              <p className="text-sm text-gray-500 mt-2">
                Upload one video that may help support the report.
              </p>
              {videoPreview && videoFile ? (
                <div className="mt-5 relative">
                  <video
                    src={videoPreview}
                    controls
                    className="w-full max-h-72 rounded-2xl border bg-black"
                  />
                  <div className="mt-3 bg-gray-50 rounded-xl p-3">
                    <p className="text-sm font-medium text-gray-700 truncate">
                      {videoFile.name}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {formatFileSize(videoFile.size)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (videoPreview) {
                        URL.revokeObjectURL(videoPreview);
                      }
                      setVideoPreview("");
                      setVideoFile(null);
                    }}
                    className="mt-3 w-full bg-red-50 text-red-600 border border-red-200 py-3 rounded-xl font-semibold hover:bg-red-100"
                  >
                    <i className="ri-delete-bin-line mr-2" />
                    Remove Video
                  </button>
                </div>
              ) : (
                <label className="mt-5 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-2xl p-8 cursor-pointer hover:border-orange-400 transition">
                  <i className="ri-video-add-line text-4xl text-orange-500 mb-2" />
                  <span className="font-medium text-gray-600">
                    Click to Upload Video
                  </span>
                  <span className="text-xs text-gray-400 mt-1">
                    MP4 • WEBM • MOV • AVI
                  </span>
                  <span className="text-xs text-orange-500 mt-2">
                    Maximum size: 50 MB
                  </span>
                  <input
                    type="file"
                    accept="video/mp4,video/webm,video/quicktime,video/x-msvideo"
                    onChange={handleVideo}
                    className="hidden"
                  />
                </label>
              )}
            </div>
            {/* NEXT BUTTONS */}
            <div className="grid grid-cols-2 gap-3 mt-6">
              <button
                onClick={back}
                className="bg-gray-100 py-4 rounded-2xl font-semibold"
              >
                Back
              </button>
              <button
                onClick={next}
                className="bg-orange-500 hover:bg-orange-600 text-white py-4 rounded-2xl font-bold"
              >
                Continue
              </button>

            </div>
          </>
        )}

        {step === 3 && (
          <>
            <div className="space-y-5">

              {/* Privacy */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-5">
                <div className="flex gap-4">

                  <div className="w-12 h-12 rounded-xl bg-yellow-100 flex items-center justify-center">
                    <i className="ri-shield-check-line text-yellow-600 text-xl" />
                  </div>

                  <div>
                    <h2 className="font-bold text-lg text-gray-800">
                      Reporter Information
                    </h2>

                    <p className="text-sm text-gray-500 mt-1">
                      Your information is confidential and will only be used by the
                      Municipal Social Welfare and Development Office during the
                      investigation.
                    </p>
                  </div>

                </div>
              </div>

              {/* Anonymous */}
              <label className="flex items-start gap-4 border rounded-2xl p-5 cursor-pointer">

                <input
                  type="checkbox"
                  checked={anonymous}
                  onChange={(e) => setAnonymous(e.target.checked)}
                  className="mt-1"
                />

                <div>
                  <h3 className="font-semibold">
                    Submit Anonymously
                  </h3>

                  <p className="text-sm text-gray-500">
                    Your identity will not be shown to other people.
                  </p>
                </div>

              </label>

              {/* Reporter Details */}
              {!anonymous && (
                <div className="bg-white border rounded-2xl p-5">

                  <h2 className="font-bold text-gray-800 mb-5">
                    Reporter Details
                  </h2>

                  <div className="grid md:grid-cols-2 gap-5">

                    <div>
                      <label className="text-sm font-semibold">
                        Full Name
                      </label>

                      <input
                        value={reporter}
                        onChange={(e) => setReporter(e.target.value)}
                        className="mt-2 w-full border rounded-xl p-3"
                      />
                    </div>

                    <div>
                      <label className="text-sm font-semibold">
                        Contact Number
                      </label>

                      <input
                        value={contact}
                        onChange={(e) => setContact(e.target.value)}
                        className="mt-2 w-full border rounded-xl p-3"
                      />
                    </div>

                  </div>

                  <div className="grid md:grid-cols-2 gap-5 mt-5">

                    <div>
                      <label className="text-sm font-semibold">
                        Relationship to the Child
                      </label>

                      <select
                        value={reporterRelation}
                        onChange={(e) => setReporterRelation(e.target.value)}
                        className="mt-2 w-full border rounded-xl p-3"
                      >
                        <option value="">Select</option>
                        <option>Mother</option>
                        <option>Father</option>
                        <option>Guardian</option>
                        <option>Relative</option>
                        <option>Teacher</option>
                        <option>Neighbor</option>
                        <option>Friend</option>
                        <option>Barangay Official</option>
                        <option>Social Worker</option>
                        <option>Concerned Citizen</option>
                        <option>Others</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-sm font-semibold">
                        Report Source
                      </label>

                      <select
                        value={reportSource}
                        onChange={(e) => setReportSource(e.target.value)}
                        className="mt-2 w-full border rounded-xl p-3"
                      >
                        <option>Victim Mobile Application</option>
                        <option>Walk-in</option>
                        <option>Barangay Referral</option>
                        <option>Police Referral</option>
                        <option>School Referral</option>
                        <option>Hospital Referral</option>
                        <option>Telephone</option>
                        <option>Facebook</option>
                        <option>Email</option>
                        <option>Others</option>
                      </select>
                    </div>

                  </div>

                  <div className="mt-5">
                    <label className="text-sm font-semibold">
                      Additional Notes
                    </label>

                    <textarea
                      rows={5}
                      value={reporterNotes}
                      onChange={(e) => setReporterNotes(e.target.value)}
                      placeholder="Additional information you want MSWD to know..."
                      className="mt-2 w-full border rounded-xl p-3 resize-none"
                    />
                  </div>

                </div>
              )}

              {/* Navigation Buttons */}
              <div className="grid grid-cols-2 gap-3">

                <button
                  onClick={back}
                  className="bg-gray-100 rounded-2xl py-4 font-semibold"
                >
                  Back
                </button>

                <button
                  onClick={next}
                  className="bg-orange-500 hover:bg-orange-600 text-white rounded-2xl py-4 font-bold"
                >
                  Continue
                </button>

              </div>

            </div>
          </>
        )}

        {step === 4 && (
          <>
            <h2 className="text-2xl font-bold text-gray-800">
              Review Report
            </h2>

            <p className="text-gray-500 mb-6">
              Please review all information before submitting.
            </p>

            {/* INCIDENT */}

            <div className="bg-white rounded-2xl border border-gray-100 p-5">

              <h3 className="font-bold text-lg text-orange-600 mb-4">
                Incident Information
              </h3>

              <ReviewItem label="Incident Type" value={incident} />
              <ReviewItem label="Priority" value={urgency} />
              <ReviewItem label="Location" value={location} />
              <ReviewItem label="Description" value={description} />

            </div>

            {/* CHILD */}

            <div className="bg-white rounded-2xl border border-gray-100 p-5 mt-5">

              <h3 className="font-bold text-lg text-blue-600 mb-4">
                Child Information
              </h3>

              <ReviewItem
                label="Full Name"
                value={`${childFirstName} ${childMiddleName} ${childLastName}`}
              />

              <ReviewItem label="Nickname" value={childNickname} />

              <ReviewItem label="Birthday" value={birthDate} />

              <ReviewItem label="Age" value={age} />

              <ReviewItem label="Gender" value={gender} />

              <ReviewItem label="Nationality" value={nationality} />

              <ReviewItem label="Religion" value={religion} />

              <ReviewItem label="Place of Birth" value={placeOfBirth} />

            </div>

            {/* ADDRESS */}

            <div className="bg-white rounded-2xl border border-gray-100 p-5 mt-5">

              <h3 className="font-bold text-lg text-green-600 mb-4">
                Address
              </h3>

              <ReviewItem label="Address" value={address} />
              <ReviewItem label="Barangay" value={barangay} />
              <ReviewItem label="Municipality" value={municipality} />
              <ReviewItem label="Province" value={province} />

            </div>

            {/* SCHOOL */}

            <div className="bg-white rounded-2xl border border-gray-100 p-5 mt-5">

              <h3 className="font-bold text-lg text-indigo-600 mb-4">
                School Information
              </h3>

              <ReviewItem label="School" value={schoolName} />
              <ReviewItem label="Grade Level" value={gradeLevel} />
              <ReviewItem label="Section" value={sectionName} />

            </div>

            {/* FAMILY */}

            <div className="bg-white rounded-2xl border border-gray-100 p-5 mt-5">

              <h3 className="font-bold text-lg text-purple-600 mb-4">
                Family Information
              </h3>

              <ReviewItem label="Father" value={fatherName} />
              <ReviewItem label="Mother" value={motherName} />
              <ReviewItem label="Guardian" value={guardianName} />
              <ReviewItem label="Relationship" value={guardianRelationship} />
              <ReviewItem label="Guardian Contact" value={guardianContact} />
              <ReviewItem label="Guardian Address" value={guardianAddress} />

            </div>

            {/* MEDICAL */}

            <div className="bg-white rounded-2xl border border-gray-100 p-5 mt-5">

              <h3 className="font-bold text-lg text-red-600 mb-4">
                Medical Information
              </h3>

              <ReviewItem
                label="Medical Conditions"
                value={medicalConditions}
              />

              <ReviewItem
                label="Medications"
                value={medications}
              />

              <ReviewItem
                label="Disability"
                value={withDisability}
              />

            </div>

            {/* PHOTOS */}

            <div className="bg-white rounded-2xl border border-gray-100 p-5 mt-5">

              <h3 className="font-bold text-lg text-gray-700 mb-4">
                Photo Evidence
              </h3>

              {photos.length > 0 ? (

                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">

                  {photos.map((photo,index)=>(

                    <img
                      key={index}
                      src={photo}
                      className="rounded-xl h-24 w-full object-cover border"
                    />

                  ))}

                </div>

              ) : (

                <p className="text-gray-500">
                  No photo uploaded.
                </p>

              )}

            </div>

            {/* VIDEO EVIDENCE */}

            <div className="bg-white rounded-2xl border border-gray-100 p-5 mt-5">

              <h3 className="font-bold text-lg text-purple-600 mb-4">
                Video Evidence
              </h3>

              {videoPreview && videoFile ? (
                <div>

                  <video
                    src={videoPreview}
                    controls
                    className="w-full max-h-72 rounded-xl border bg-black"
                  />

                  <div className="mt-3 bg-gray-50 rounded-xl p-3">
                    <p className="text-sm font-semibold text-gray-700 truncate">
                      {videoFile.name}
                    </p>

                    <p className="text-xs text-gray-500 mt-1">
                      {formatFileSize(videoFile.size)}
                    </p>
                  </div>

                </div>
              ) : (
                <p className="text-gray-500">
                  No video uploaded.
                </p>
              )}

            </div>

            {/* REPORTER */}

            <div className="bg-white rounded-2xl border border-gray-100 p-5 mt-5">

              <h3 className="font-bold text-lg text-yellow-600 mb-4">
                Reporter Information
              </h3>

              <ReviewItem
                label="Anonymous"
                value={anonymous ? "Yes" : "No"}
              />

              {!anonymous && (
                <>

                  <ReviewItem label="Reporter" value={reporter} />

                  <ReviewItem
                    label="Contact"
                    value={contact}
                  />

                  <ReviewItem
                    label="Relationship"
                    value={reporterRelation}
                  />

                  <ReviewItem
                    label="Report Source"
                    value={reportSource}
                  />

                  <ReviewItem
                    label="Additional Notes"
                    value={reporterNotes}
                  />

                </>
              )}

            </div>

            {submitMessage && (
              <div className="bg-green-50 border border-green-200 rounded-2xl p-4 mt-5">
                <p className="text-green-700">
                  {submitMessage}
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 mt-6">

              <button
                onClick={back}
                className="bg-gray-100 py-4 rounded-2xl font-semibold"
              >
                Back
              </button>

              <button
                onClick={submitReport}
                disabled={submitting}
                className="bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white py-4 rounded-2xl font-bold"
              >
                {submitting ? "Submitting..." : "Submit Report"}
              </button>

            </div>

          </>
        )}
      </div>

      {assignedOffice && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="assigned-office-title"
        >
          <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <i className="ri-checkbox-circle-line text-4xl text-green-600" />
            </div>

            <h2
              id="assigned-office-title"
              className="text-center text-xl font-bold text-gray-800"
            >
              Report Submitted Successfully
            </h2>

            <p className="mt-2 text-center text-sm text-gray-600">
              Your report has been recorded. Please take note of your assigned
              DSWD office.
            </p>

            <div className="mt-5 rounded-2xl border border-orange-200 bg-orange-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-orange-700">
                Your Assigned Office
              </p>

              <h3 className="mt-2 text-lg font-bold text-gray-900">
                {assignedOffice.name}
              </h3>

              {assignedOffice.location && (
                <p className="mt-1 flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-map-pin-line mt-0.5" />
                  <span>{assignedOffice.location}</span>
                </p>
              )}

              {assignedOffice.reportUid && (
                <p className="mt-3 text-xs text-gray-600">
                  Report reference: {assignedOffice.reportUid}
                </p>
              )}
            </div>

            <p className="mt-4 text-sm leading-6 text-gray-600">
              Please contact or visit your assigned DSWD office for assistance.
              Bring your report reference number if needed.
            </p>

            <button
              type="button"
              onClick={() => {
                window.location.reload();
              }}
              className="mt-6 w-full rounded-xl bg-orange-600 px-4 py-3 font-semibold text-white transition hover:bg-orange-700"
            >
              Done
            </button>

          </div>
        </div>
      )}
    </AppLayout>
  );
}

function StepIndicator({ step }: { step: number }) {
  const steps = ['Incident', 'Details', 'Contact', 'Review'];

  return (
    <div className="flex items-center justify-between">
      {steps.map((label, index) => {
        const number = index + 1;
        const active = step >= number;

        return (
          <div key={label} className="flex-1 flex items-center">
            <div className="flex flex-col items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                active ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-400'
              }`}>
                {step > number ? <i className="ri-check-line" /> : number}
              </div>
              <span className={`text-[10px] mt-1 ${active ? 'text-orange-600' : 'text-gray-400'}`}>
                {label}
              </span>
            </div>
            {number < 4 && (
              <div className={`flex-1 h-0.5 mx-2 ${step > number ? 'bg-orange-500' : 'bg-gray-100'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function EmergencyBox({ onCall }: { onCall: () => void }) {
  return (
    <button
      type="button"
      onClick={onCall}
      className="w-full bg-red-50 border border-red-200 rounded-2xl p-4 flex items-center gap-3 text-red-600 text-left hover:bg-red-100 transition"
    >
      <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
        <i className="ri-phone-fill" />
      </div>
      <div className="flex-1">
        <h3 className="font-semibold">Is this an emergency?</h3>
        <p className="text-xs">
          Tap to call the nearest emergency hotline based on your location
        </p>
      </div>

      <i className="ri-arrow-right-s-line" />
    </button>
  );
}

function UrgencyCard({
  active,
  color,
  title,
  desc,
  onClick,
}: {
  active: boolean;
  color: 'red' | 'yellow' | 'green';
  title: string;
  desc: string;
  onClick: () => void;
}) {
  const styles = {
    red: 'border-red-300 bg-red-50 text-red-500',
    yellow: 'border-yellow-300 bg-yellow-50 text-yellow-500',
    green: 'border-green-300 bg-green-50 text-green-500',
  };

  return (
    <button
      onClick={onClick}
      className={`w-full border rounded-2xl p-4 text-left flex gap-3 ${styles[color]} ${
        active ? 'ring-2 ring-orange-200' : ''
      }`}
    >
      <div className="w-3 h-3 rounded-full bg-current mt-1.5" />
      <div>
        <h3 className="font-bold text-gray-800">{title}</h3>
        <p className="text-sm text-gray-500">{desc}</p>
      </div>
    </button>
  );
}

function ReviewItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-gray-50 rounded-2xl p-4">
      <p className="uppercase text-xs text-gray-400">{label}</p>
      <p className="font-semibold text-gray-800 mt-1">{value}</p>
    </div>
  );
}